/**
 * React entry point — wires QueryClient + TanStack Router and mounts the
 * app under #root. The router tree is generated by the TanStack Router
 * Vite plugin from the files under src/routes/.
 *
 * Two cross-cutting concerns wired here:
 *   - **Global 401 handler** via QueryCache/MutationCache `onError`. When any
 *     query or mutation hits a 401 (session expired, logged-out in another
 *     tab, cookies cleared) we flip the cached `me` to null. The root route
 *     watches that and redirects to /welcome — same path as a fresh visitor.
 *   - **Top-level error boundary** so an uncaught render error becomes a
 *     friendly card instead of a blank #root.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { APIError } from "./lib/api";
import "./app.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => count < 2 && err instanceof APIError && err.status >= 500,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
});

function handleAuthError(err: unknown) {
  if (err instanceof APIError && err.status === 401) {
    // Mark the user as signed-out without a refetch — the root route's
    // useEffect compares this value and redirects to /welcome.
    queryClient.setQueryData(["auth", "me"], null);
  }
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultErrorComponent: RouteErrorFallback,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function RouteErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-6">
      <div className="card max-w-md p-6 text-center">
        <h1 className="text-base font-semibold text-heading mb-2">Something went sideways.</h1>
        <p className="text-sm text-subtle mb-4">
          {error.message || "Unknown error rendering this page."}
        </p>
        <a href="/" className="btn btn-primary inline-block">
          Back to dashboard
        </a>
      </div>
    </div>
  );
}

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return <RouteErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

const root = document.getElementById("root")!;
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
